import { createSlice, PayloadAction} from "@reduxjs/toolkit";
import {RequestError} from "../Entity/RequestError";
import {AppThunk} from "../../app/store";
import {Cart, CartDto} from "../Entity/Cart";
import {
  addItemInCartRep,
  changeQuantityItemInCartRep,
  createCartRep,
  getCartRep,
  removeItemFromCartRep, submitCartRep
} from "../Repository/CartRepository";
import {PutItemToCartOfferRequest} from "../Entity/PutItemToCartRequest";
import {LocalParams} from "../../components/LocalParams";

interface CartState {
  isRequesting: boolean,
  isRequestSuccess: boolean,
  isAddSuccess: boolean,
  isRequestFailed: boolean,
  isRequestingDelete: boolean,
  isSubmitSuccess: boolean,
  error: RequestError | null,
  cart: Cart|null,
  isAddFailed: boolean,
  tokenCart: null,
  isRequestingSubmitCart: boolean,
  isFailedSubmitCart: boolean
}

const CartInitialState: CartState = {
  isRequesting: false,
  isRequestSuccess: false,
  isAddSuccess: false,
  isRequestFailed: false,
  isRequestingDelete: false,
  isSubmitSuccess: false,
  cart: null,
  error: null,
  isAddFailed: false,
  tokenCart: null,
  isRequestingSubmitCart: false,
  isFailedSubmitCart: false,
}

function startRequest(state: CartState) {
  state.isRequesting = true;
  state.isRequestingDelete = false;
  state.isRequestFailed = false;
  state.isAddSuccess = false;
  state.isSubmitSuccess = false;
  state.cart = null;
  state.isAddFailed= false;
}

function registerFailed(state: CartState, action: PayloadAction<any>) {
  state.isRequesting = false;
  state.isRequestSuccess = false;
  state.isRequestFailed = true;
  state.isAddSuccess = false;
  state.isSubmitSuccess = false;
  state.error = action.payload;
}

function startRemoveRequest(state: CartState) {
  state.isRequesting = false;
  state.isRequestingDelete = true;
  state.isRequestFailed = false;
  state.cart = null;
}

function registerSuccess(state: CartState, action: PayloadAction<any>) {
  state.isRequesting = false;
  state.isRequestSuccess = true;
  state.isRequestFailed = false;
  state.isRequestingDelete = false;
  state.isSubmitSuccess = false;
  state.error = null;
  state.cart = action.payload;
}

function addSuccess(state: CartState, action: PayloadAction<any>) {
  state.isRequesting = false;
  state.isAddSuccess = true;
  state.isAddFailed = false;
  state.error = null;
  state.cart = action.payload;
}

function addFailed(state: CartState, action: PayloadAction<any>) {
  state.isRequesting = false;
  state.isAddSuccess = true;
  state.isAddFailed = false;
  state.error = null;
  state.cart = action.payload;
}
function submitCartRequest(state: CartState) {
  state.isRequestingSubmitCart = true;
  state.isRequestingDelete = false;
  state.isFailedSubmitCart = false;
}
function submitCartFailed(state: CartState, action: PayloadAction<any>) {
  state.isRequestingSubmitCart = false;
  state.isFailedSubmitCart = true;
  state.isSubmitSuccess = false;
  state.error = null;
  state.cart = action.payload;
}
function submitCartSuccess(state: CartState, action: PayloadAction<any>) {
  state.isRequesting = false;
  state.isRequestingSubmitCart = false;
  state.isRequestSuccess = false;
  state.isSubmitSuccess = true;
  state.isFailedSubmitCart = false;
  state.isRequestingDelete = false;
  state.error = null;
  state.cart = action.payload;
}


function cartClear(state: CartState) {
  state.isRequesting = false;
  state.isRequestSuccess = false;
  state.isSubmitSuccess = true;
  state.isRequestFailed = false;
  state.isRequestingDelete = false;
  state.error = null;
  state.cart = null;
  state.tokenCart = null;
}

const cartSlice = createSlice({
    name: 'cart',
    initialState: CartInitialState,
    reducers: {
      CartStartRequest: startRequest,
      CartSuccess: registerSuccess,
      CartFailed: registerFailed,
      CartStartRemoveRequest : startRemoveRequest,
      CartSubmitSuccess : submitCartSuccess,
      CartAddSuccess: addSuccess,
      CartAddFailed: addFailed,
      CartClear : cartClear,
      CartSubmitRequest: submitCartRequest,
      CartSubmitFailed : submitCartFailed
    }
  }
)

export const {
  CartStartRequest,
  CartSuccess,
  CartFailed,
  CartStartRemoveRequest,
  CartSubmitSuccess,
  CartAddSuccess,
  CartAddFailed,
  CartClear,
  CartSubmitRequest,
  CartSubmitFailed
} = cartSlice.actions;

export default cartSlice.reducer;

export const getCart = (tokenCart : string): AppThunk => async dispatch => {
  try {
    dispatch(CartStartRequest());
    const newsApiResponse = await getCartRep(tokenCart);
    dispatch(CartSuccess(newsApiResponse));
  } catch (err) {
    dispatch(CartFailed(err));
  }
}


export const createCartAndAddItem = (locale: string, cartData: PutItemToCartOfferRequest, userEmail: string|null): AppThunk => async dispatch => {
  try {
    dispatch(CartStartRequest());
    const createCartApiResponse = await createCartRep(locale, userEmail);
    const tokenCart = LocalParams.getItem('tokenCart') as string
    const addItemApiResponse = await addItemInCartRep(cartData, tokenCart);
    dispatch(CartAddSuccess(addItemApiResponse));
  } catch (err) {
    dispatch(CartAddFailed(err));
  }
}


export const addItemToCart = (cartData: PutItemToCartOfferRequest, tokenCart: string): AppThunk => async dispatch => {
  try {
    dispatch(CartStartRequest());
    const newsApiResponse = await addItemInCartRep(cartData, tokenCart);
    dispatch(CartAddSuccess(newsApiResponse));
  } catch (err) {
    dispatch(CartAddFailed(err));
  }
}

export const removeItemFromCart = (tokenCart: string, itemId: number): AppThunk => async dispatch => {
  try {
    dispatch(CartStartRemoveRequest());
    const newsApiResponse = await removeItemFromCartRep(tokenCart, itemId);
    dispatch(CartSuccess(newsApiResponse));
  } catch (err) {
    dispatch(CartFailed(err));
  }
}

export const changeQuantityItemInCart = (tokenCart: string, itemId: number, quantity: number): AppThunk => async dispatch => {
  try {
    dispatch(CartStartRequest());
    const newsApiResponse = await changeQuantityItemInCartRep(tokenCart, itemId, quantity);
    dispatch(CartSuccess(newsApiResponse));
  } catch (err) {
    dispatch(CartFailed(err));
  }
}

export const submitCart = (cartData: CartDto, tokenValue: string): AppThunk => async dispatch => {
  try {
    dispatch(CartSubmitRequest());
    const newsApiResponse = await submitCartRep(cartData, tokenValue);
    LocalParams.removeItem('tokenCart');
    dispatch(CartClear());
    dispatch(CartSubmitSuccess(newsApiResponse));
  } catch (err) {
    dispatch(CartSubmitFailed(err));
  }
}

